import { supabase } from "@/services/supabaseClient";
import { resolveConsultationModeAuthority } from "../../supabase/functions/_shared/consultationModeAuthority.mjs";

export class DoctorBookingAuthorityError extends Error {
  constructor(code = "booking_authority_unavailable", cause = null) {
    super(code);
    this.name = "DoctorBookingAuthorityError";
    this.code = code;
    this.cause = cause || null;
  }
}

function normalizeDirectMode(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";
  if (raw === "evisit" || raw === "virtual" || raw === "telehealth" || raw === "private") {
    return "evisit";
  }
  if (raw === "inperson" || raw === "in-person" || raw === "moh" || raw === "onsite") {
    return "inperson";
  }
  return "";
}

function inferRequestedMode(requestedMode, consultationType, hasFacilityContext = false) {
  const explicitMode = normalizeDirectMode(requestedMode);
  if (explicitMode) return explicitMode;

  const fromConsultationType = normalizeDirectMode(consultationType);
  if (fromConsultationType) return fromConsultationType;

  return hasFacilityContext ? "inperson" : "";
}

function compactAddress(parts = []) {
  return parts
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");
}

function normalizeFacilityOption(option) {
  if (!option || typeof option !== "object") return null;

  const id = String(option?.id || option?.facilityId || "").trim();
  const name = String(option?.name || option?.facilityName || "").trim();
  const address = String(
    option?.address ||
      compactAddress([option?.facility_address, option?.street_address, option?.city, option?.country])
  ).trim();
  const city = String(option?.city || option?.facility_city || "").trim();
  const country = String(option?.country || option?.facility_country || "").trim();
  const lat = option?.lat ?? option?.facility_lat ?? option?.facility_latitude ?? null;
  const lng =
    option?.lng ??
    option?.lon ??
    option?.longitude ??
    option?.facility_lng ??
    option?.facility_longitude ??
    null;

  if (!id && !name && !address) return null;

  return {
    id: id || null,
    name: name || "Facility",
    address,
    city,
    country,
    lat,
    lng,
  };
}

function dedupeFacilityOptions(items = []) {
  const seen = new Set();
  return items.filter((item) => {
    const normalized = normalizeFacilityOption(item);
    if (!normalized) return false;

    const key =
      normalized.id ||
      `${String(normalized.name || "").trim().toLowerCase()}::${String(normalized.address || "")
        .trim()
        .toLowerCase()}`;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function facilityMatchesRequest(facility, request = {}) {
  if (!facility) return false;

  const requestedId = String(request?.facilityId || request?.facility?.id || "").trim();
  const requestedName = String(request?.facilityName || request?.facility?.name || "").trim().toLowerCase();
  const requestedAddress = String(request?.facility?.address || "").trim().toLowerCase();

  if (requestedId && String(facility?.id || "").trim() === requestedId) return true;
  if (requestedName && String(facility?.name || "").trim().toLowerCase() === requestedName) return true;
  if (
    requestedName &&
    String(facility?.name || "").trim().toLowerCase().includes(requestedName)
  ) {
    return true;
  }
  if (
    requestedAddress &&
    String(facility?.address || "").trim().toLowerCase() === requestedAddress
  ) {
    return true;
  }

  return false;
}

function buildFallbackFacility(doctor = null, request = {}) {
  const fallback = normalizeFacilityOption({
    id:
      request?.facilityId ||
      request?.facility?.id ||
      doctor?.facility_id ||
      doctor?.facility?.id ||
      null,
    name:
      request?.facilityName ||
      request?.facility?.name ||
      doctor?.facility?.name ||
      doctor?.hospital ||
      doctor?.displayLocation ||
      "",
    address:
      request?.facility?.address ||
      doctor?.facility?.address ||
      doctor?.address ||
      doctor?.mapAddress ||
      "",
    city: request?.facility?.city || doctor?.facility?.city || "",
    country: request?.facility?.country || doctor?.facility?.country || "",
    lat: request?.facility?.lat ?? doctor?.facility?.lat ?? doctor?.lat ?? null,
    lng: request?.facility?.lng ?? doctor?.facility?.lng ?? doctor?.lng ?? null,
  });

  return fallback;
}

async function optionalSelect(query) {
  try {
    const { data, error } = await query;
    if (error) return [];
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

export async function loadDoctorBookingContext({ doctorId, doctor = null, facility = null } = {}) {
  const requestedDoctorId = String(
    doctorId || doctor?.id || doctor?.user_id || doctor?.doctor_user_id || ""
  ).trim();
  const fallbackFacility = buildFallbackFacility(doctor, { facility });

  if (!requestedDoctorId) {
    throw new DoctorBookingAuthorityError("booking_authority_identifier_missing");
  }

  const { data: authorityRows, error: authorityError } = await supabase.rpc(
    "get_patient_booking_authority",
    { p_doctor_reference: requestedDoctorId }
  );
  if (authorityError) {
    throw new DoctorBookingAuthorityError("booking_authority_read_failed", authorityError);
  }
  const authority = Array.isArray(authorityRows) ? authorityRows[0] : authorityRows;
  if (!authority?.doctor_user_id || !authority?.doctor_profile_id) {
    throw new DoctorBookingAuthorityError("booking_authority_not_available");
  }

  const lookupIds = Array.from(
    new Set(
      [requestedDoctorId, authority?.doctor_profile_id, authority?.doctor_user_id]
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  );

  const [consultancies, legacyLocations] =
    lookupIds.length > 0
      ? await Promise.all([
          optionalSelect(
            supabase
              .from("doctor_consultancies")
              .select(
                "id,doctor_id,facility_name,facility_address,facility_city,facility_country,is_primary,sort_order,updated_at"
              )
              .in("doctor_id", lookupIds)
              .order("is_primary", { ascending: false })
              .order("sort_order", { ascending: true })
              .order("updated_at", { ascending: false })
          ),
          optionalSelect(
            supabase
              .from("doctor_consulting_locations")
              .select("id,doctor_id,facility_name,street_address,city,country,updated_at")
              .in("doctor_id", lookupIds)
              .order("updated_at", { ascending: false })
          ),
        ])
      : [[], []];

  const facilities = dedupeFacilityOptions([
    ...(consultancies || []).map((row) => ({
      id: row?.id || null,
      name: row?.facility_name || "",
      address: compactAddress([row?.facility_address, row?.facility_city, row?.facility_country]),
      city: row?.facility_city || "",
      country: row?.facility_country || "",
    })),
    ...(legacyLocations || []).map((row) => ({
      id: row?.id || null,
      name: row?.facility_name || "",
      address: compactAddress([row?.street_address, row?.city, row?.country]),
      city: row?.city || "",
      country: row?.country || "",
    })),
    ...(fallbackFacility ? [fallbackFacility] : []),
  ]);

  const resolvedAuthority = resolveConsultationModeAuthority({
    practiceMode: authority.practice_mode,
    evisitEnabled: authority.evisit_enabled,
    inPersonReady: authority.in_person_ready,
  });

  return {
    doctorId: String(authority.doctor_user_id).trim(),
    profile: {
      id: authority.doctor_profile_id,
      user_id: authority.doctor_user_id,
      practice_mode: authority.practice_mode,
      evisit_enabled: authority.evisit_enabled,
    },
    authorityVersion: authority.authority_version || null,
    inPersonReady: authority.in_person_ready === true,
    evisitEnabled: authority.evisit_enabled === true,
    ...resolvedAuthority,
    facilities,
    hasInPersonOption: resolvedAuthority.inPersonAvailable,
    defaultFacility: facilities[0] || null,
  };
}

export async function resolveDoctorBookingEntryState({
  doctorId,
  doctor = null,
  requestedMode = null,
  consultationType = null,
  facilityId = null,
  facilityName = "",
  facility = null,
} = {}) {
  const requestedFacilityContext = {
    facilityId,
    facilityName,
    facility,
  };
  const hasFacilityContext = Boolean(
    facilityId ||
      facility?.id ||
      facilityName ||
      facility?.name ||
      doctor?.facility_id ||
      doctor?.facility?.id ||
      doctor?.facility?.name ||
      doctor?.hospital
  );
  const preferredMode = inferRequestedMode(requestedMode, consultationType, hasFacilityContext);
  const context = await loadDoctorBookingContext({
    doctorId,
    doctor,
    facility,
  });
  const resolvedAuthority = resolveConsultationModeAuthority({
    practiceMode: context.practiceMode,
    evisitEnabled: context.evisitEnabled,
    inPersonReady: context.inPersonReady,
    preferredMode,
    hasFacilityContext,
  });
  const mode = resolvedAuthority.mode;

  const matchedFacility =
    context.facilities.find((option) => facilityMatchesRequest(option, requestedFacilityContext)) ||
    context.defaultFacility ||
    buildFallbackFacility(doctor, requestedFacilityContext) ||
    null;

  return {
    ...context,
    ...resolvedAuthority,
    mode,
    facilityId: mode === "inperson" ? matchedFacility?.id || null : null,
    facilityName: mode === "inperson" ? matchedFacility?.name || "" : "",
    facility: mode === "inperson" ? matchedFacility : null,
    modeChanged: Boolean(preferredMode) && mode !== preferredMode,
  };
}
